'use client'

import * as React from 'react'
import { usePathname } from 'next/navigation'
import { createPortal } from 'react-dom'
import { X, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { useUIStore } from '@/lib/store'

interface TourStep {
  target: string | string[] // CSS selector(s) for element(s) to highlight, empty for centered modal
  title: string
  content: React.ReactNode
  position: 'top' | 'bottom' | 'left' | 'right' | 'center'
}

// Home-page tour. The gate (signed-out `/`) hides filter chips, so the
// filter-centric steps only make sense on the signed-in home. buildHomeSteps
// omits them when filters aren't on the page (detected at open) — otherwise
// the "Use Filters" step spotlighted a missing element and described controls
// that weren't there.
function buildHomeSteps(filtersAvailable: boolean): TourStep[] {
  const welcome: TourStep = {
    target: '',
    title: 'Welcome to Roostr! 👋',
    content: (
      <p>Find your perfect apartment in seconds. This quick tour will show you how our search works.</p>
    ),
    position: 'center',
  }

  const city: TourStep = {
    target: '[data-tour="city-selector"]',
    title: filtersAvailable ? 'Step 1: Choose Your City' : 'Choose Your City',
    content: <p>First, pick the city (and optionally a district) you want to search in.</p>,
    position: 'bottom',
  }

  const filters: TourStep = {
    target: '[data-tour="more-filters"]',
    title: 'Step 2: Use Filters',
    content: (
      <div className="space-y-3">
        <p><strong>Filters</strong> are the quick buttons you see here. Click any to apply it instantly.</p>
        <p className="text-xs text-muted-foreground">Click <strong>&quot;Filters&quot;</strong> for 50+ detailed options: heating type, kitchen appliances, building features, and more.</p>
      </div>
    ),
    position: 'bottom',
  }

  const naturalLanguage: TourStep = {
    target: '[data-tour="search-input"]',
    title: filtersAvailable ? 'Step 3: Type Naturally' : 'Type Naturally',
    content: (
      <div className="space-y-3">
        <p>Type like you&apos;re texting a friend. Our AI understands <strong>location</strong>, <strong>price</strong>, <strong>rooms</strong>, and <strong>amenities</strong> — all at once, no filters needed.</p>
        <div className="bg-muted/50 rounded-lg p-3 space-y-2">
          <div>
            <p className="text-xs font-medium text-foreground">Location &amp; commute:</p>
            <p className="text-xs text-muted-foreground">&quot;in Praha 3&quot; · &quot;near Florenc&quot; · &quot;15 min from Anděl&quot;</p>
          </div>
          <div>
            <p className="text-xs font-medium text-foreground">Price:</p>
            <p className="text-xs text-muted-foreground">&quot;under 25k&quot; · &quot;between 15k and 30k&quot;</p>
          </div>
          <div>
            <p className="text-xs font-medium text-foreground">Rooms &amp; amenities:</p>
            <p className="text-xs text-muted-foreground">&quot;2+kk&quot; · &quot;balcony&quot; · &quot;dishwasher&quot; · &quot;parking&quot;</p>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">Need inspiration? Tap <strong>&quot;More examples&quot;</strong> for ready-made queries.</p>
      </div>
    ),
    position: 'bottom',
  }

  const twoWays: TourStep = {
    target: ['[data-tour="search-input"]', '[data-tour="more-filters"]'],
    title: 'Two Ways to Search',
    content: (
      <div className="space-y-3">
        <p>Use <strong>natural language</strong> to describe what you want, or pick <strong>filters</strong> manually — whichever you prefer!</p>
        <p className="text-xs text-muted-foreground">Each method works independently. Searching by text resets filters, and applying filters clears the text.</p>
      </div>
    ),
    position: 'center',
  }

  const finish: TourStep = {
    target: '',
    title: "You're all set 🎉",
    content: (
      // Auth-neutral: this journey shows on the gate AND the signed-in home
      // (both filterless). Guests get the sign-in prompt when they save.
      <p>Run a search now — <strong>save it</strong> to get <strong>instant alerts</strong> the moment a new match goes live.</p>
    ),
    position: 'center',
  }

  return filtersAvailable
    ? [welcome, city, filters, naturalLanguage, twoWays]
    : [welcome, city, naturalLanguage, finish]
}

// Steps shown on the search results page (different elements available)
const SEARCH_TOUR_STEPS: TourStep[] = [
  {
    target: '',
    title: 'Welcome to Roostr! 👋',
    content: (
      <p>Find your perfect apartment in seconds. This quick tour will show you how our search works.</p>
    ),
    position: 'center',
  },
  {
    target: '',
    title: 'Natural Language Search',
    content: (
      <div className="space-y-3">
        <p>Use the <strong>search bar in the header</strong> to search naturally. Our AI understands location, price, rooms, and amenities.</p>
        <div className="bg-muted/50 rounded-lg p-3 space-y-2">
          <div>
            <p className="text-xs font-medium text-foreground">Examples:</p>
            <p className="text-xs text-muted-foreground">&quot;2 rooms in Praha 3 under 25k with balcony&quot;</p>
            <p className="text-xs text-muted-foreground">&quot;15 min from Anděl, parking, dishwasher&quot;</p>
          </div>
        </div>
      </div>
    ),
    position: 'center',
  },
  {
    target: '[data-tour="view-toggle"]',
    title: 'Switch Views',
    content: (
      <p>Toggle between <strong>list view</strong> and <strong>map view</strong> to see your results in different ways.</p>
    ),
    position: 'bottom',
  },
  {
    target: '',
    title: 'Active Filters',
    content: (
      <p>Your search criteria appear as <strong>chips</strong> below the result count. Click the × on any chip to remove that filter and update results.</p>
    ),
    position: 'center',
  },
]

export function SearchTour() {
  const pathname = usePathname()
  const { tourOpen: open, setTourOpen, completeTour } = useUIStore()
  const onClose = React.useCallback(() => setTourOpen(false), [setTourOpen])
  const onComplete = React.useCallback(() => completeTour(), [completeTour])

  const [currentStep, setCurrentStep] = React.useState(0)
  const [mounted, setMounted] = React.useState(false)
  const [targetRects, setTargetRects] = React.useState<DOMRect[]>([])
  // Whether the filter chips are actually on the page (gate hides them).
  // Latched when the tour opens so the step list is stable across steps.
  const [filtersAvailable, setFiltersAvailable] = React.useState(false)

  // Select steps based on which page we're on
  const TOUR_STEPS = React.useMemo(
    () => (pathname === '/search' ? SEARCH_TOUR_STEPS : buildHomeSteps(filtersAvailable)),
    [pathname, filtersAvailable]
  )

  const totalSteps = TOUR_STEPS.length
  const step = TOUR_STEPS[currentStep]
  const isLastStep = currentStep === totalSteps - 1
  const targets = Array.isArray(step.target) ? step.target : (step.target ? [step.target] : [])
  const hasTargets = targets.length > 0
  const isCentered = step.position === 'center' || !hasTargets

  React.useEffect(() => {
    setMounted(true)
  }, [])

  // Reset step + re-detect filter chips when opening
  React.useEffect(() => {
    if (open) {
      setCurrentStep(0)
      setFiltersAvailable(!!document.querySelector('[data-tour="more-filters"]'))
    }
  }, [open])

  // Update target rects when step changes
  React.useEffect(() => {
    if (!open || !hasTargets) {
      setTargetRects([])
      return
    }

    const updateRects = () => {
      const rects: DOMRect[] = []
      targets.forEach(selector => {
        const element = document.querySelector(selector)
        if (element) {
          rects.push(element.getBoundingClientRect())
        }
      })
      setTargetRects(rects)
    }

    updateRects()
    window.addEventListener('resize', updateRects)
    window.addEventListener('scroll', updateRects, true)

    return () => {
      window.removeEventListener('resize', updateRects)
      window.removeEventListener('scroll', updateRects, true)
    }
  }, [open, currentStep, isCentered, targets.join(',')])

  const handleNext = () => {
    if (isLastStep) {
      onComplete()
      onClose()
    } else {
      setCurrentStep((prev) => prev + 1)
    }
  }

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1)
    }
  }

  const handleSkip = () => {
    onComplete()
    onClose()
  }

  if (!mounted || !open) return null

  // Calculate tooltip position
  const getTooltipStyle = (): React.CSSProperties => {
    if (isCentered || targetRects.length === 0) {
      // Center in viewport
      return {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      }
    }

    // Use first rect for positioning
    const targetRect = targetRects[0]
    const padding = 16
    const tooltipWidth = 380

    switch (step.position) {
      case 'bottom':
        return {
          position: 'fixed',
          top: targetRect.bottom + padding,
          left: Math.max(padding, Math.min(
            targetRect.left + targetRect.width / 2 - tooltipWidth / 2,
            window.innerWidth - tooltipWidth - padding
          )),
        }
      case 'top':
        return {
          position: 'fixed',
          bottom: window.innerHeight - targetRect.top + padding,
          left: Math.max(padding, Math.min(
            targetRect.left + targetRect.width / 2 - tooltipWidth / 2,
            window.innerWidth - tooltipWidth - padding
          )),
        }
      case 'left':
        return {
          position: 'fixed',
          top: targetRect.top + targetRect.height / 2,
          right: window.innerWidth - targetRect.left + padding,
          transform: 'translateY(-50%)',
        }
      case 'right':
        return {
          position: 'fixed',
          top: targetRect.top + targetRect.height / 2,
          left: targetRect.right + padding,
          transform: 'translateY(-50%)',
        }
      default:
        return {}
    }
  }

  const content = (
    <div className="fixed inset-0 z-[200]">
      {/* Overlay with spotlight cutout */}
      <div className="absolute inset-0">
        {targetRects.length > 0 ? (
          <svg className="absolute inset-0 w-full h-full">
            <defs>
              <mask id="spotlight-mask">
                <rect x="0" y="0" width="100%" height="100%" fill="white" />
                {targetRects.map((rect, index) => (
                  <rect
                    key={index}
                    x={rect.left - 8}
                    y={rect.top - 8}
                    width={rect.width + 16}
                    height={rect.height + 16}
                    rx="8"
                    fill="black"
                  />
                ))}
              </mask>
            </defs>
            <rect
              x="0"
              y="0"
              width="100%"
              height="100%"
              fill="rgba(0, 0, 0, 0.75)"
              mask="url(#spotlight-mask)"
            />
          </svg>
        ) : (
          <div className="absolute inset-0 bg-black/75" />
        )}
      </div>

      {/* Highlight borders around targets */}
      {targetRects.map((rect, index) => (
        <div
          key={index}
          className="absolute border-2 border-primary rounded-lg pointer-events-none animate-pulse"
          style={{
            left: rect.left - 8,
            top: rect.top - 8,
            width: rect.width + 16,
            height: rect.height + 16,
          }}
        />
      ))}

      {/* Tooltip */}
      <div
        className={cn(
          'w-[380px] bg-background border border-border shadow-lg elevation-lg rounded-xl',
          'animate-in fade-in-0 zoom-in-95 duration-200'
        )}
        style={getTooltipStyle()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-4 pb-2">
          <div className="flex items-center gap-2">
            {(currentStep === 0 || isLastStep) && <Sparkles className="h-5 w-5 text-primary" />}
            <h3 className="font-semibold text-foreground">{step.title}</h3>
          </div>
          <button
            onClick={handleSkip}
            className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Content */}
        <div className="px-4 pb-4 text-sm text-muted-foreground leading-relaxed">
          {step.content}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-border/50 bg-muted/20 rounded-b-xl">
          <div className="flex items-center gap-2">
            {/* Progress dots */}
            <div className="flex gap-1">
              {TOUR_STEPS.map((_, index) => (
                <div
                  key={index}
                  className={cn(
                    'w-1.5 h-1.5 rounded-full transition-colors',
                    index === currentStep ? 'bg-primary' : 'bg-muted-foreground/30'
                  )}
                />
              ))}
            </div>
            <span className="text-xs text-muted-foreground ml-2">
              {currentStep + 1} of {totalSteps}
            </span>
          </div>

          <div className="flex items-center gap-2">
            {currentStep > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handlePrev}
                className="h-8"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
            )}
            <Button
              size="sm"
              onClick={handleNext}
              className="h-8"
            >
              {isLastStep ? 'Get Started' : 'Next'}
              {!isLastStep && <ChevronRight className="h-4 w-4 ml-1" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )

  return createPortal(content, document.body)
}
